import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './i18n';
import Index from './pages/Index';
import Index2 from './pages/Index2';
import Index3 from './pages/Index3';
import Index4 from './pages/Index4';
import FeaturesPage from './pages/Features';
import AboutPage from './pages/About';
import BookDemoPage from './pages/BookDemo';
import SignUpPage from './pages/SignUp';
import PressPage from './pages/Press';
import LearnMorePage from './pages/LearnMore';
import SecurityPage from './pages/Security';
import EnterprisePage from './pages/Enterprise';
import DocumentationPage from './pages/Documentation';
import HelpCenterPage from './pages/HelpCenter';
import APIPage from './pages/API';
import CareersPage from './pages/Careers';
import ApplyNow from './pages/ApplyNow';
import ContactPage from './pages/Contact';
import Contact2Page from './pages/Contact2';
import PrivacyPage from './pages/Privacy';
import LegalDisclosurePage from './pages/LegalDisclosure';
import TermsPage from './pages/Terms';
import CustomizeWorkflowPage from './pages/CustomizeWorkflow';
import ComplianceAI from './pages/ComplianceAI';
import ComplianceCalendar from './pages/ComplianceCalendar';
import HorizonScanning from './pages/HorizonScanning';
import ToolsPage from './pages/Tools';
import PlatformPage from './pages/Platform';
import AIFMDPage from './pages/AIFMD';
import CSRDPage from './pages/CSRD';
import NIS2Page from './pages/NIS2';
import CTAFormPage from './pages/l/ctaform';
import SmallCTAPage from './pages/l/smallcta';
import AIFMDLanding20 from './pages/l/20/aifmd';
import AIFMDLanding22 from './pages/l/22/aifmd';
import FinanceMarketingLanding from './pages/l/23/finance-marketing';
import AIFMDLanding23 from './pages/l/23/aifmd';
import LuthorAILanding from './pages/l/24/luthor-ai';
import FinanceMarketingWilsonLanding from './pages/l/24/finance-marketing-wilson';
import VercLanding from './pages/l/24/verc';
import TeamworkInspiredLanding from './pages/l/teamwork-inspired';
import TMNGLanding from './pages/l/tmng';
import MinimalLanding from './pages/l/minimal';
import AIFMDMarketingLanding from './pages/l/AIFMDMarketing';
import LandingDirectoryPage from './pages/landing';
import ShortCodeRedirect from './components/ShortCodeRedirect';

import WorkflowVisualizer from './pages/WorkflowVisualizer';
import HotjarScript from './components/HotjarScript';
import GoogleAnalytics from './components/GoogleAnalytics';
import LinkedInTrackingScript from './components/LinkedInTrackingScript';
import { trackPageViewEnhanced } from '@/utils/analytics';
import metadataJson from '@/data/metatags.json';
import { CookieBanner } from './components/CookieBanner';
import { useCookieConsent } from './hooks/useCookieConsent';

// Type for metadata
interface MetadataItem {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogUrl: string;
  ogType: string;
  twitterCard: string;
  canonical: string;
  keywords: string;
}

interface MetadataCollection {
  [path: string]: MetadataItem;
}

// Type assertion for imported JSON
const metadata = metadataJson as MetadataCollection;

const queryClient = new QueryClient();

const ScrollToTop = () => {
  const { pathname, search } = useLocation();
  const { t } = useTranslation('common');
  const { consent } = useCookieConsent();

  useEffect(() => {
    // Scroll to top when route changes
    window.scrollTo(0, 0);

    // Get page title from metadata if available
    const metadataPath = metadata[pathname] ? pathname : '/';
    const meta = metadata[metadataPath];

    // Translation keys based on path
    const translationNamespace = metadataPath === '/' ? 'homepage' : metadataPath.replace('/', ''); // Remove leading slash

    // Get page title from translations or metadata
    const pageTitle =
      t(`${translationNamespace}.helmet.title`, { defaultValue: meta?.title }) || document.title;

    // Only track page views if user has consented to analytics
    if (consent?.analytics) {
      trackPageViewEnhanced(pathname, pageTitle, { search });
    }

    // For debugging
    if (import.meta.env.DEV) {
      console.log('Route changed:', {
        path: pathname,
        title: pageTitle,
        search,
      });
    }
  }, [pathname, search, t, consent]);

  return null;
};

const App = () => {
  const isDevelopment = import.meta.env.DEV;
  const { consent } = useCookieConsent();

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <HelmetProvider>
          <Toaster />
          <Sonner />
          {/*{isDevelopment && <DebugPanel/>}*/}
          {consent?.analytics && (
            <>
              <HotjarScript hjid={3876765} hjsv={6} />
              <GoogleAnalytics measurementId="G-ZL8PE90PQ4" />
            </>
          )}
          {consent?.marketing && <LinkedInTrackingScript partnerId="8216449" />}
          <BrowserRouter>
            <ScrollToTop />
            <CookieBanner />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/index1" element={<Index />} />
              <Route path="/index2" element={<Index2 />} />
              <Route path="/index3" element={<Index3 />} />
              <Route path="/index4" element={<Index4 />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/book-demo" element={<BookDemoPage />} />
              <Route path="/press" element={<PressPage />} />
              <Route path="/learn-more" element={<LearnMorePage />} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/enterprise" element={<EnterprisePage />} />
              <Route path="/documentation" element={<DocumentationPage />} />
              <Route path="/help" element={<HelpCenterPage />} />
              <Route path="/api" element={<APIPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/careers/apply/:roleId" element={<ApplyNow />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/contact2" element={<Contact2Page />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/legal-disclosure" element={<LegalDisclosurePage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/customize" element={<CustomizeWorkflowPage />} />
              <Route path="/visualizer" element={<WorkflowVisualizer />} />
              <Route path="/compliance-ai" element={<ComplianceAI />} />
              <Route path="/compliance-calendar" element={<ComplianceCalendar />} />
              <Route path="/horizon-scanning" element={<HorizonScanning />} />
              <Route path="/tools" element={<ToolsPage />} />
              <Route path="/platform" element={<PlatformPage />} />
              <Route path="/l/ctaform" element={<CTAFormPage />} />
              <Route path="/l/smallcta" element={<SmallCTAPage />} />

              {/* Framework Pages */}
              <Route path="/aifmd" element={<AIFMDPage />} />
              <Route path="/csrd" element={<CSRDPage />} />
              <Route path="/nis2" element={<NIS2Page />} />

              {/* Landing Pages */}
              <Route path="/landing" element={<LandingDirectoryPage />} />
              <Route path="/l/20/aifmd" element={<AIFMDLanding20 />} />
              <Route path="/l/22/aifmd" element={<AIFMDLanding22 />} />
              <Route path="/l/23/aifmd" element={<AIFMDLanding23 />} />
              <Route path="/l/23/finance-marketing" element={<FinanceMarketingLanding />} />
              <Route path="/l/24/luthor-ai" element={<LuthorAILanding />} />
              <Route path="/l/24/fmw" element={<FinanceMarketingWilsonLanding />} />
              <Route path="/l/24/verc" element={<VercLanding />} />
              <Route path="/l/teamwork-inspired" element={<TeamworkInspiredLanding />} />
              <Route path="/l/tmng" element={<TMNGLanding />} />

              <Route path="/l/minimal" element={<MinimalLanding />} />
              <Route path="/regtechai" element={<MinimalLanding />} />
              <Route path="/how-to-optimize-regulations" element={<MinimalLanding />} />
              <Route path="/l/AIFMDMarketing" element={<AIFMDMarketingLanding />} />

              {/* Short code redirects (e.g., /ES000N5C) */}
              <Route path="/:code" element={<ShortCodeRedirect />} />

              {/* Catch-all route for 404 - redirects to homepage */}
              <Route path="*" element={<Navigate replace to="/" />} />
            </Routes>
          </BrowserRouter>
        </HelmetProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
